/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useContext, useEffect, useState } from "react";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import useFetchModel from "hooks/useFetchModel";
import { useAuth } from "cms/auth/hooks/use-auth";
import { WebpageSelectorWrapper } from "components/webpage-selector/webpage-selector-wrapper";
import { isObject, isStringNotEmpty, objectHasPropertyString } from "utils/empty-exists";
import { cloneObjectsArray } from "utils/array";
import { getDefaultWebpageCardStyle } from "../helpers";
import { ComponentFeatureWebpageContext } from "./component-feature-webpage-context";
import { Logger } from "cms/utils/log/logger";

const WebpageCard = () => {
    
    const { token } = useAuth();
    const fetchModel = useFetchModel();
    const {
        list,
        item,
        index,
        style: _style,
        updateItems
    } = useContext(ComponentFeatureWebpageContext);
    
    const [webpage, setWebpage] = useState();
    const [model, setModel] = useState();
    const [webpageGuid, setWebpageGuid] = useState();
    const [loadingWebpage, setLoadingWebpage] = useState(false);
    const { webpage: guid } = item || {};
    const [style, setStyle] = useState(_style || "default");
    
    const loadWebpage = async () => {
        if (isStringNotEmpty(guid)) {
            const _webpage = await SimpliCityAdminSDK.webpage.findOne(token, guid);
            setWebpage(_webpage);
            const _style = getDefaultWebpageCardStyle(_webpage);
            setStyle(_style);
            return _webpage;
        }
    };

    useEffect(() => {
        const load = async () => {
            if (item && Object.keys(item).length > 0) {
                setLoadingWebpage(true);
                setWebpageGuid(item);
                const _webpage = await loadWebpage(guid);
                setWebpage(_webpage);
                let _style = getDefaultWebpageCardStyle(_webpage);
                // if we already have a selection for style use it
                _style = isStringNotEmpty(style) ? style : _style;
                setStyle(_style);
                setLoadingWebpage(false);
            }
        };
        // load once
        if (!isObject(webpage) || !objectHasPropertyString(webpage, "guid") || webpage?.guid !== guid) {
            load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item]);

    useEffect(() => {
        if (isObject(webpage) && objectHasPropertyString(webpage, "guid")) {
            const style = getDefaultWebpageCardStyle(webpage);
            setStyle(style);
        }
    }, [webpage]);
    
    const onChangeWebpage = async (selectedWebpage) => {
        setWebpage(selectedWebpage);
        const style = getDefaultWebpageCardStyle(selectedWebpage);
        setStyle(style);
        const nextWebpages = cloneObjectsArray(list);
        nextWebpages[index].webpage = selectedWebpage?.guid;

        if (selectedWebpage?.model) {
            Logger.debug(selectedWebpage?.model);
            const newModel = await fetchModel(token, selectedWebpage?.model);
            const image = newModel?.banner_image?.image;
            setModel({ ...newModel, banner: image });
            nextWebpages[index].title = newModel?.title;
            nextWebpages[index].description = newModel?.description;
            nextWebpages[index].image = image;
        }
        Logger.debug(nextWebpages);
        updateItems(nextWebpages);
    };
    const onRemoveWebpage = async () => {
        setWebpage(undefined);
        setWebpageGuid(undefined);
        setStyle("default");
        const nextWebpages = cloneObjectsArray(list);
        nextWebpages[index].webpage = "";
        nextWebpages[index].image = "";
        nextWebpages[index].title = "";
        nextWebpages[index].description = "";
        updateItems(nextWebpages);
    };

    const defaultTitleDescription = () => {
        const nextWebpages = list;
        updateItems(nextWebpages);
    };

    const defaultImage = () => {
        const nextWebpages = cloneObjectsArray(list);
        updateItems(nextWebpages);
    };

    const changeTitle = (newTitle) => {
        const nextWebpages = cloneObjectsArray(list);
        nextWebpages[index].title = newTitle;
        updateItems(nextWebpages);
    };

    const changeDescription = (newDescription) => {
        const nextWebpages = cloneObjectsArray(list);
        nextWebpages[index].description = newDescription;
        updateItems(nextWebpages);
    };

    return (
        <div style={{ flex: "1", border: "1px solid #CFCFCF", padding: 12, borderRadius: 4 }}>
            <WebpageSelectorWrapper
                idx={index}
                selectedData={item}
                webpage={webpage}
                setWebpage={setWebpage}
                model={model}
                webpageGuid={webpageGuid}
                setWebpageGuid={setWebpageGuid}
                onWebpageChange={(webpage) => onChangeWebpage(webpage)}
                onWebpageRemove={() => onRemoveWebpage()}
                disabled={false}
                loadingWebpage={loadingWebpage}
                setLoadingWebpage={setLoadingWebpage}
                defaultImage={defaultImage}
                defaultTitleDescription={defaultTitleDescription}
                changeTitle={changeTitle}
                changeDescription={changeDescription}
            />
        </div>
    );
};

WebpageCard.displayName = "WebpageCard";
export { WebpageCard };
